import { Button, type ButtonProps } from "@/components/ui/button";
import { Spinner } from "@/components/ui/Loader/Spinner";
import { cn } from "@/utils/cn";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";

const DialogCardVariants = cva(
  "bg-white flex w-full max-w-[520px] flex-col gap-6 rounded-lg border border-edge-light px-4 py-5 sm:p-6",
  {
    variants: {
      align: {
        center: "items-center justify-center text-center",
        start: "items-start justify-start text-left",
        between: "items-start justify-between",
      },
    },
    defaultVariants: {
      align: "start",
    },
  },
);

type DialogCardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof DialogCardVariants>;

export const DialogCard = React.forwardRef<HTMLDivElement, DialogCardProps>(
  ({ className, align, ...props }, ref) => (
    <div
      data-component="DialogCard"
      ref={ref}
      className={cn(DialogCardVariants({ align }), className)}
      {...props}
    />
  ),
);

DialogCard.displayName = "DialogCard";

const DialogCardHeaderVariants = cva("flex self-stretch", {
  variants: {
    direction: {
      row: "flex-row items-center",
      col: "flex-col",
    },
    spacing: {
      none: "gap-0",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    },
  },
  defaultVariants: {
    direction: "row",
    spacing: "xs",
  },
});

type DialogCardHeaderProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof DialogCardHeaderVariants>;

export const DialogCardHeader = React.forwardRef<
  HTMLDivElement,
  DialogCardHeaderProps
>(({ className, direction, spacing, ...props }, ref) => (
  <div
    data-component="DialogCardHeader"
    ref={ref}
    className={cn(DialogCardHeaderVariants({ direction, spacing }), className)}
    {...props}
  />
));
DialogCardHeader.displayName = "DialogCardHeader";

export const DialogCardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    data-component="DialogCardTitle"
    ref={ref}
    className={cn(
      "font-archivo text-base font-medium text-[#323232] sm:text-2xl",
      className,
    )}
    {...props}
  />
));
DialogCardTitle.displayName = "DialogCardTitle";

export const DialogCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    data-component="DialogCardDescription"
    ref={ref}
    className={cn(
      "self-stretch font-archivo text-sm font-light tracking-[0.14px] text-[#6C6C6C] sm:leading-[23.8px]",
      className,
    )}
    {...props}
  />
));
DialogCardDescription.displayName = "DialogCardDescription";

export const DialogCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    data-component="DialogCardContent"
    ref={ref}
    className={cn("grid w-full gap-4", className)}
    {...props}
  />
));
DialogCardContent.displayName = "DialogCardContent";

export const DialogCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    data-component="DialogCardFooter"
    ref={ref}
    className={cn(
      "flex items-center justify-center gap-3 self-stretch",
      className,
    )}
    {...props}
  />
));
DialogCardFooter.displayName = "DialogCardFooter";

export const DialogCardActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    data-component="DialogCardActions"
    ref={ref}
    className={cn(
      "flex flex-col-reverse gap-3 self-stretch",
      "sm:flex-row sm:items-center sm:justify-end sm:gap-4",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));

DialogCardActions.displayName = "DialogCardActions";

interface DialogCardCTAProps extends ButtonProps {
  isLoading?: boolean;
}

export const DialogCardCTA = React.forwardRef<
  HTMLButtonElement,
  DialogCardCTAProps
>(({ className, variant = "default", isLoading, children, ...props }, ref) => (
  <Button
    data-component="DialogCardCTA"
    ref={ref}
    variant={variant}
    className={cn("w-full", className)}
    disabled={isLoading || props.disabled}
    {...props}
  >
    {isLoading ? <Spinner /> : children}
  </Button>
));
DialogCardCTA.displayName = "DialogCardCTA";
